import { useCallback, useEffect, useMemo, useState } from 'react';
import type { LogbookEntryType } from '../types/logbook';
import { NOTE_TYPE_CODE } from '../types/logbook';
import {
  getLogbookEntryTypes,
  createLogbookEntryType,
  updateLogbookEntryType,
  type LogbookEntryTypeCreateDto,
  type LogbookEntryTypePatchDto,
} from '../lib/api/endpoints';
import { logbookEntryTypeFromDto } from '../lib/api/mappers';

/**
 * FE-13 — el catálogo de tipos de novedad del tenant.
 *
 * Se pide SIEMPRE completo (con los inactivos) y se filtra en memoria, porque
 * las dos superficies que lo consumen necesitan cosas opuestas: el formulario
 * de registro solo puede ofrecer los activos —un tipo desactivado se retiró a
 * propósito—, mientras que la pantalla de administración tiene que ver los
 * inactivos para poder reactivarlos. Pedirlo dos veces con `?active=` sería
 * dos viajes por una lista de siete filas que cambia una vez al año.
 *
 * `crear` y `actualizar` lanzan `ApiError` tal cual: el `403` del Supervisor y
 * el `409` de código repetido son información de negocio, y quien llama es
 * quien sabe cómo contarlos.
 */
export function useLogbookTypes() {
  const [types, setTypes] = useState<LogbookEntryType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const res = await getLogbookEntryTypes();
        if (cancelled) return;
        setTypes(res.items.map(logbookEntryTypeFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setTypes([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando el catálogo de novedades'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  const upsert = useCallback((type: LogbookEntryType) => {
    setTypes(prev => {
      const idx = prev.findIndex(t => t.id === type.id);
      if (idx === -1) return [...prev, type];
      const next = [...prev];
      next[idx] = type;
      return next;
    });
  }, []);

  const acciones = useMemo(() => ({
    crear: async (data: LogbookEntryTypeCreateDto): Promise<LogbookEntryType> => {
      const type = logbookEntryTypeFromDto(await createLogbookEntryType(data));
      upsert(type);
      return type;
    },
    // Solo la etiqueta y el interruptor: `code` no viaja porque no se corrige.
    actualizar: async (typeId: string, data: LogbookEntryTypePatchDto): Promise<LogbookEntryType> => {
      const type = logbookEntryTypeFromDto(await updateLogbookEntryType(typeId, data));
      upsert(type);
      return type;
    },
  }), [upsert]);

  const activos = useMemo(
    () => types.filter(t => t.active).sort((a, b) => a.label.localeCompare(b.label)),
    [types],
  );

  // El tipo que sostiene la novedad libre. Puede faltar: un Administrador
  // puede haberlo desactivado, y entonces el tenant se quedó sin dónde
  // escribir notas. La UI lo dice en vez de fingir que existe.
  const tipoNota = useMemo(() => activos.find(t => t.code === NOTE_TYPE_CODE), [activos]);

  return { types, activos, tipoNota, isLoading, error, reload, ...acciones };
}
